import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { BookOpen, Download, Search, ChevronRight, Film, Trash2 } from "lucide-react";
import { VideoPlayer } from "../lib/video";

// Video tutorial per sezione: admin può caricare un file o incollare un URL (YouTube/Vimeo/mp4)
function SectionVideo({ section, url, admin, onChange }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(url || "");
  const [busy, setBusy] = useState(false);
  const save = async (v) => {
    setBusy(true);
    try { const { data } = await api.put("/manual/videos", { section: section.title, video_url: v }); onChange(data.videos); setEdit(false); toast.success(v ? "Video collegato" : "Video rimosso"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/uploads/video", fd, { headers: { "Content-Type": "multipart/form-data" } }); await save(data.url); }
    catch (err) { toast.error(formatApiError(err)); setBusy(false); }
  };
  if (!url && !admin) return null;
  return (
    <div className="mb-4" data-testid={`manual-video-${section.id}`}>
      {url && <div className="rounded-xl overflow-hidden bg-black ring-1 ring-zinc-800"><VideoPlayer url={url} background={false} className="w-full aspect-video" testid={`manual-video-player-${section.id}`} /></div>}
      {admin && (
        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
          {!edit ? (
            <>
              <button type="button" onClick={() => { setVal(url || ""); setEdit(true); }} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-zinc-700 text-zinc-300 hover:bg-zinc-900" data-testid={`manual-video-edit-${section.id}`}><Film className="h-3 w-3" /> {url ? "Cambia video tutorial" : "Aggiungi video tutorial"}</button>
              {url && <button type="button" onClick={() => save("")} disabled={busy} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-red-800/50 text-red-400 hover:bg-red-500/10" data-testid={`manual-video-remove-${section.id}`}><Trash2 className="h-3 w-3" /> Rimuovi</button>}
            </>
          ) : (
            <>
              <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder="URL YouTube / Vimeo / .mp4" className="h-8 text-xs bg-zinc-950 border-zinc-800 flex-1 min-w-[220px] font-mono" data-testid={`manual-video-url-${section.id}`} />
              <label className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-zinc-700 text-zinc-300 hover:bg-zinc-900 cursor-pointer"><Film className="h-3 w-3" /> {busy ? "Carico…" : "Carica file"}<input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={upload} disabled={busy} data-testid={`manual-video-upload-${section.id}`} /></label>
              <Button size="sm" onClick={() => save(val)} disabled={busy} className="h-8 text-xs bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]" data-testid={`manual-video-save-${section.id}`}>Salva</Button>
              <Button size="sm" variant="outline" onClick={() => setEdit(false)} className="h-8 text-xs border-zinc-800" data-testid={`manual-video-cancel-${section.id}`}>Annulla</Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9àèéìòù]+/g, "-").replace(/^-|-$/g, "");
const text = (n) => (typeof n === "string" ? n : Array.isArray(n) ? n.map(text).join("") : n?.props?.children ? text(n.props.children) : "");

// Divide il markdown in sezioni "## " per indice e ricerca
function splitSections(md) {
  const out = []; let cur = { title: "Introduzione", body: [] };
  md.split("\n").forEach((l) => {
    if (l.startsWith("## ")) { out.push(cur); cur = { title: l.slice(3).trim(), body: [l] }; } else cur.body.push(l);
  });
  out.push(cur);
  return out.map((s) => ({ ...s, md: s.body.join("\n"), id: slug(s.title) })).filter((s) => s.md.trim());
}

export default function Manual() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [docs, setDocs] = useState([]);
  useEffect(() => { api.get("/manual").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))); api.get("/manual/docs").then((r) => setDocs(r.data)).catch(() => {}); }, []);
  const sections = useMemo(() => (data ? splitSections(data.markdown) : []), [data]);
  const shown = useMemo(() => {
    const k = q.trim().toLowerCase();
    return k ? sections.filter((s) => s.md.toLowerCase().includes(k)) : sections;
  }, [sections, q]);
  const downloadPdf = async (path = "/manual/pdf", name = "manuale-vivi-fidelity.pdf") => {
    setDownloading(true);
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); } finally { setDownloading(false); }
  };
  const components = {
    h2: ({ children }) => <h2 id={slug(text(children))} className="font-heading text-2xl font-bold mt-8 mb-3 scroll-mt-24 text-[color:var(--paprika)]">{children}</h2>,
    h3: ({ children }) => <h3 id={slug(text(children))} className="font-heading text-lg font-semibold mt-5 mb-2 scroll-mt-24">{children}</h3>,
    p: ({ children }) => <p className="text-sm leading-relaxed text-zinc-300 mb-2">{children}</p>,
    li: ({ children }) => <li className="text-sm leading-relaxed text-zinc-300">{children}</li>,
    ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 mb-3">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 mb-3">{children}</ol>,
    code: ({ children }) => <code className="rounded bg-zinc-900 border border-zinc-800 px-1.5 py-0.5 text-[12px] font-mono text-amber-200 break-all">{children}</code>,
    a: ({ href, children }) => <a href={href} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">{children}</a>,
    blockquote: ({ children }) => <blockquote className="border-l-2 border-[color:var(--paprika)] pl-3 text-zinc-400 italic mb-3">{children}</blockquote>,
    table: ({ children }) => <div className="overflow-x-auto mb-4"><table className="w-full text-sm border border-zinc-800 rounded-lg overflow-hidden">{children}</table></div>,
    th: ({ children }) => <th className="bg-zinc-900 text-left px-3 py-2 text-xs uppercase tracking-wide text-zinc-400 border-b border-zinc-800">{children}</th>,
    td: ({ children }) => <td className="px-3 py-2 border-b border-zinc-800/60 text-zinc-300 align-top">{children}</td>,
  };
  return (
    <div className="space-y-4 animate-fade-up" data-testid="manual-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <span className="pill bg-zinc-200 text-zinc-700"><BookOpen className="h-3 w-3" /> GUIDA</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Manuale dell'app</h1>
          {data && <p className="text-xs text-zinc-500 mt-1" data-testid="manual-meta">Aggiornato il {new Date(data.updated_at).toLocaleDateString("it-IT")} · versione {data.version}{!data.admin && " · sezione tecnica riservata all'admin"}</p>}
        </div>
        <div className="flex items-center gap-3">
          <img src="/icons/nouvelle-fidelity.png" alt="Nouvelle Fidelity" className="h-14 w-auto hidden sm:block" data-testid="manual-platform-logo" draggable={false} />
          <Button onClick={() => downloadPdf()} disabled={!data || downloading} data-testid="manual-download-pdf" className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"><Download className="h-4 w-4 mr-1" /> {downloading ? "Preparo…" : "Scarica PDF"}</Button>
        </div>
      </div>
      {docs.length > 0 && (
        <div className="card-elev p-4" data-testid="manual-docs">
          <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Documenti tecnici e analisi</div>
          <div className="space-y-1.5">
            {docs.map((d) => (
              <div key={d.slug} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2" data-testid={`manual-doc-${d.slug}`}>
                <div className="min-w-0"><div className="text-sm font-semibold text-zinc-100 truncate">{d.title}</div><div className="text-[11px] text-zinc-500">Aggiornato il {new Date(d.updated_at).toLocaleDateString("it-IT")}</div></div>
                <Button size="sm" variant="outline" className="border-zinc-700 h-8 text-xs" disabled={downloading} onClick={() => downloadPdf(`/manual/docs/${d.slug}/pdf`, `${d.slug.toLowerCase()}.pdf`)} data-testid={`manual-doc-pdf-${d.slug}`}><Download className="h-3 w-3 mr-1" /> PDF</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nel manuale (es. webhook, coupon, PayPal)…" className="pl-9 bg-zinc-950 border-zinc-800" data-testid="manual-search" />
      </div>
      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <nav className="sticky top-6 card-elev p-3 max-h-[80vh] overflow-y-auto space-y-0.5" data-testid="manual-toc">
            {shown.map((s) => <a key={s.id} href={`#${s.id}`} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white px-2 py-1.5 rounded-md hover:bg-zinc-900" data-testid={`manual-toc-${s.id}`}><ChevronRight className="h-3 w-3 shrink-0" /><span className="truncate">{s.title}</span></a>)}
          </nav>
        </aside>
        <article className="card-elev p-5 sm:p-8 min-w-0" data-testid="manual-content">
          {!data && <p className="text-sm text-zinc-500">Caricamento…</p>}
          {data && shown.length === 0 && <p className="text-sm text-zinc-500" data-testid="manual-empty">Nessuna sezione contiene "{q}".</p>}
          {q && shown.length > 0 && <p className="text-xs text-zinc-500 mb-4" data-testid="manual-search-count">{shown.length} sezioni trovate</p>}
          {shown.map((s) => {
            const body = s.body.filter((l) => !l.startsWith("## ")).join("\n");
            return (
              <section key={s.id} data-testid={`manual-section-${s.id}`}>
                {s.body[0]?.startsWith("## ") && <h2 id={s.id} className="font-heading text-2xl font-bold mt-8 mb-3 scroll-mt-24 text-[color:var(--paprika)]">{s.title}</h2>}
                <SectionVideo section={s} url={data.videos?.[s.id]} admin={data.admin} onChange={(videos) => setData({ ...data, videos })} />
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{body}</ReactMarkdown>
              </section>
            );
          })}
        </article>
      </div>
    </div>
  );
}
